import posthog from 'posthog-js';
import { env } from 'configs/EnvironmentConfig';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { decodeToken, getUserEmailFromToken, getUserIdFromToken } from 'utils/jwtUtils';

export const FEEDBACK_MILESTONE_EVENT = 'vlm:feedback-milestone';

const STORAGE_PREFIX = 'vlm_feedback';
let posthogInitialized = false;

const normalizeUserKey = (value) =>
  String(value || 'anonymous')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9@._-]/g, '_');

export const getFeedbackUserKey = () => {
  const token = localStorage.getItem(SEC_AUTH_M_TK);
  return normalizeUserKey(
    getUserIdFromToken(token) ||
      getUserEmailFromToken(token) ||
      localStorage.getItem('_un')
  );
};

const storageKey = (category, name, userKey = getFeedbackUserKey()) =>
  `${STORAGE_PREFIX}:${normalizeUserKey(userKey)}:${category}:${name}`;

export const initPostHog = () => {
  if (posthogInitialized || !env?.POSTHOG_KEY) return posthogInitialized;

  posthog.init(env.POSTHOG_KEY, {
    api_host: env.POSTHOG_HOST || 'https://us.i.posthog.com',
    // Initial pageview is captured below; SPA navigations call capturePageview().
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    person_profiles: 'identified_only',
  });
  posthogInitialized = true;
  posthog.capture('$pageview');
  return true;
};

export const capturePageview = () => {
  if (initPostHog()) {
    posthog.capture('$pageview');
  }
};

export const identifyFeedbackUser = () => {
  const token = localStorage.getItem(SEC_AUTH_M_TK);
  if (!token) return null;

  const payload = decodeToken(token) || {};
  const email = getUserEmailFromToken(token) || localStorage.getItem('_un') || undefined;
  const distinctId = getUserIdFromToken(token) || email;

  if (distinctId && initPostHog()) {
    posthog.identify(String(distinctId), {
      email,
      role: payload.role,
      subscription_type: payload.subscription_type,
      tenant_id: payload.tenant_id,
    });
  }

  return { distinctId, email, payload };
};

export const captureFeedbackEvent = (eventName, properties = {}) => {
  if (initPostHog()) {
    posthog.capture(eventName, properties);
  }
};

const USER_SIGNED_UP_EVENT = 'user_signed_up';

/**
 * Identify the user and capture `user_signed_up` once per newly created account.
 * Safe no-op when PostHog is unavailable. Never throws to callers.
 * Call only after successful account creation (authSlice signup paths).
 */
export const captureUserSignedUp = (user = {}) => {
  try {
    // Fail gracefully if PostHog key is missing / init fails
    if (!initPostHog()) return false;

    const token = localStorage.getItem(SEC_AUTH_M_TK);
    const email =
      user.email ||
      getUserEmailFromToken(token) ||
      localStorage.getItem('_un') ||
      undefined;
    const distinctId =
      user.id ||
      user.distinctId ||
      getUserIdFromToken(token) ||
      email;

    if (!distinctId) return false;

    // Once per newly created account
    const onceKey = storageKey('event', USER_SIGNED_UP_EVENT, distinctId);
    if (localStorage.getItem(onceKey)) return false;

    posthog.identify(String(distinctId), {
      email,
      name: user.name,
    });

    // Signup successful — PostHog survey trigger (user_signed_up)
    posthog.capture(USER_SIGNED_UP_EVENT, {
      signup_method: user.signupMethod,
      email,
    });

    localStorage.setItem(onceKey, new Date().toISOString());
    return true;
  } catch (err) {
    // Never block signup on analytics failures
    console.warn('PostHog user_signed_up failed:', err);
    return false;
  }
};

export const resetFeedbackUser = () => {
  if (posthogInitialized) {
    posthog.reset();
  }
};

export const hasSurveyState = (surveyId) =>
  Boolean(
    localStorage.getItem(storageKey('submitted', surveyId)) ||
      localStorage.getItem(storageKey('dismissed', surveyId))
  );

export const hasFeedbackMilestone = (milestone) =>
  Boolean(localStorage.getItem(storageKey('milestone', milestone)));

export const saveSurveyState = (surveyId, state) => {
  localStorage.setItem(storageKey(state, surveyId), new Date().toISOString());
};

export const recordAccountCreatedAt = (value = new Date().toISOString(), userKey) => {
  const key = storageKey('account', 'created_at', userKey);
  if (!localStorage.getItem(key)) {
    localStorage.setItem(key, value);
  }
};

export const getAccountCreatedAt = (payload = {}) => {
  const claim =
    payload.account_created_at ||
    payload.user_created_at ||
    payload.created_at ||
    payload.createdAt;
  if (claim) {
    const parsedClaim = new Date(claim);
    if (!Number.isNaN(parsedClaim.getTime())) {
      recordAccountCreatedAt(parsedClaim.toISOString());
      return parsedClaim;
    }
  }

  const stored =
    localStorage.getItem(storageKey('account', 'created_at')) ||
    localStorage.getItem(storageKey('account', 'created_at', localStorage.getItem('_un')));
  if (stored) {
    const parsedStored = new Date(stored);
    if (!Number.isNaN(parsedStored.getTime())) return parsedStored;
  }

  return null;
};

export const triggerFeedbackMilestone = (milestone, properties = {}) => {
  const markerKey = storageKey('milestone', milestone);
  const isFirstOccurrence = !localStorage.getItem(markerKey);

  if (isFirstOccurrence) {
    localStorage.setItem(markerKey, new Date().toISOString());
  }

  captureFeedbackEvent(`feedback_milestone_${milestone}`, {
    ...properties,
    first_occurrence: isFirstOccurrence,
  });

  window.dispatchEvent(
    new CustomEvent(FEEDBACK_MILESTONE_EVENT, {
      detail: { milestone, properties, isFirstOccurrence },
    })
  );
};
